import React, { useState } from 'react';
import { X, CreditCard, DollarSign, Truck, CheckCircle, Smartphone, QrCode, ArrowLeft, Tag, Check } from 'lucide-react';
import MercadoPagoCheckout from './MercadoPagoCheckout';
import toast from 'react-hot-toast';
import { useMobileOptimizations } from '../hooks/useMobileOptimizations';

interface ModalPagamentoProps {
  isOpen: boolean;
  onClose: () => void;
  valorTotal: number;
  onPagamentoConfirmado: (metodo: string, detalhes?: any) => void;
  usuario?: {
    id: string | number;
    nome: string;
    email: string;
    telefone: string;
  } | null;
  dadosCarrinho?: {
    itens: any[];
    observacoes?: string;
    valorFrete: number;
    enderecoEntrega: any;
    usuario: any;
  };
  onCupomAplicado?: (cupom: { codigo: string; promocao: any; desconto: number } | null) => void;
  validarCupom?: (codigo: string, valorTotal: number) => Promise<{ valido: boolean; promocao?: any; mensagem?: string }>;
  calcularDescontoCupom?: (valorTotal: number, promocao: any) => number;
  podeVoltar?: boolean;
  onVoltar?: () => void;
  tipoEntrega?: 'entrega' | 'retirada';
}

const ModalPagamento: React.FC<ModalPagamentoProps> = ({
  isOpen,
  onClose,
  valorTotal,
  onPagamentoConfirmado,
  usuario,
  dadosCarrinho,
  onCupomAplicado,
  validarCupom,
  calcularDescontoCupom,
  podeVoltar,
  onVoltar,
  tipoEntrega = 'entrega'
}) => {
  const { isMobile, isTablet } = useMobileOptimizations();
  const [tipoPagamento, setTipoPagamento] = useState<string>('');
  const [metodoEntrega, setMetodoEntrega] = useState<string>('');
  const [metodoSite, setMetodoSite] = useState<string>('');
  const [precisaTroco, setPrecisaTroco] = useState(false);
  const [valorTroco, setValorTroco] = useState<number>(0);
  const [loading, setLoading] = useState(false);
  const [showMetodos, setShowMetodos] = useState(false);
  const [showMercadoPago, setShowMercadoPago] = useState(false);
  const [codigoCupom, setCodigoCupom] = useState<string>('');
  const [cupomAplicado, setCupomAplicado] = useState<{ codigo: string; promocao: any; desconto: number } | null>(null);
  const [validandoCupom, setValidandoCupom] = useState(false);

  // Detectar tipo de dispositivo
  const deviceType: 'mobile' | 'tablet' | 'desktop' = isMobile ? 'mobile' : isTablet ? 'tablet' : 'desktop';

  // Funções auxiliares (ANTES de serem usadas)
  const resetForm = () => {
    setTipoPagamento('');
    setMetodoEntrega('');
    setMetodoSite('');
    setPrecisaTroco(false);
    setValorTroco(0);
    setShowMetodos(false);
    setShowMercadoPago(false);
    setCodigoCupom('');
    setCupomAplicado(null);
  };

  const handleClose = () => {
    resetForm();
    onClose();
  };

  // Prevenir scroll no overlay (especialmente mobile)
  const handleOverlayTouchMove = (e: React.TouchEvent) => {
    e.preventDefault();
  };

  const handleOverlayClick = (e: React.MouseEvent) => {
    if (e.target === e.currentTarget) {
      handleClose();
    }
  };

  // Handlers de negócio
  const handlePagamentoMercadoPago = (status: string, detalhes: any) => {
    if (status === 'approved') {
      onPagamentoConfirmado('site', {
        metodo: 'mercadopago',
        status: 'pago',
        payment_id: detalhes.payment_id
      });
      resetForm();
      onClose();
    }
  };

  const handleTipoPagamento = (tipo: string) => {
    setTipoPagamento(tipo);
    if (tipo === 'site') {
      // Automaticamente selecionar PIX para pagamento pelo site
      setMetodoSite('pix_site');
      setShowMercadoPago(true);
    } else {
      setShowMetodos(true);
    }
  };

  const handleVoltar = () => {
    setShowMetodos(false);
    setShowMercadoPago(false);
    setTipoPagamento('');
    setMetodoEntrega('');
    setMetodoSite('');
    setPrecisaTroco(false);
    setValorTroco(0);
  };

  const handleConfirmarPagamento = async () => {
    if (!tipoPagamento) {
      return;
    }

    if (tipoPagamento === 'entrega' && !metodoEntrega) {
      return;
    }

    // Para pagamento pelo site, sempre usar PIX
    if (tipoPagamento === 'site') {
      setMetodoSite('pix_site');
    }

    setLoading(true);
    try {
      let detalhes: any = {};

      if (tipoPagamento === 'entrega') {
        let mensagemTroco = '';
        if (metodoEntrega === 'dinheiro' && precisaTroco && valorTroco >= valorTotalComDesconto) {
          mensagemTroco = `Pedido de R$ ${valorTotalComDesconto.toFixed(2)}, pago com R$ ${valorTroco.toFixed(2)}. Troco de R$ ${(valorTroco - valorTotalComDesconto).toFixed(2)}.`;
        }

        detalhes = {
          metodo: metodoEntrega,
          precisaTroco,
          valorTroco: precisaTroco ? valorTroco : undefined,
          mensagemTroco: mensagemTroco || undefined
        };
      } else {
        detalhes = {
          metodo: metodoSite
        };
      }

      // Incluir cupom nos detalhes se aplicado
      const detalhesComCupom = {
        ...detalhes,
        cupom: cupomAplicado ? {
          codigo: cupomAplicado.codigo,
          promocao_id: cupomAplicado.promocao.id,
          desconto: cupomAplicado.desconto
        } : undefined
      };

      await onPagamentoConfirmado(tipoPagamento, detalhesComCupom);
    } catch (error) {
      console.error('Erro ao confirmar pagamento:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleAplicarCupom = async () => {
    if (!codigoCupom.trim() || !validarCupom || !calcularDescontoCupom) {
      return;
    }

    setValidandoCupom(true);
    try {
      const resultado = await validarCupom(codigoCupom.trim().toUpperCase(), valorTotal);

      if (resultado.valido && resultado.promocao) {
        const desconto = calcularDescontoCupom(valorTotal, resultado.promocao);
        const cupom = {
          codigo: codigoCupom.trim().toUpperCase(),
          promocao: resultado.promocao,
          desconto
        };
        setCupomAplicado(cupom);
        if (onCupomAplicado) {
          onCupomAplicado(cupom);
        }
        toast.success(`Cupom "${cupom.codigo}" aplicado com sucesso!`, {
          duration: 3000
        });
      } else {
        // Mostrar mensagem de erro mais detalhada
        const mensagemErro = resultado.mensagem || 'Cupom inválido';
        toast.error(mensagemErro, {
          duration: 5000,
          style: {
            maxWidth: '400px',
            whiteSpace: 'normal'
          }
        });
        setCupomAplicado(null);
        if (onCupomAplicado) {
          onCupomAplicado(null);
        }
      }
    } catch (error) {
      toast.error('Erro ao validar cupom');
      setCupomAplicado(null);
      if (onCupomAplicado) {
        onCupomAplicado(null);
      }
    } finally {
      setValidandoCupom(false);
    }
  };

  const handleRemoverCupom = () => {
    setCupomAplicado(null);
    setCodigoCupom('');
    if (onCupomAplicado) {
      onCupomAplicado(null);
    }
    toast.success('Cupom removido');
  };

  // Calcular valor total com desconto do cupom
  const valorTotalComDesconto = cupomAplicado
    ? Math.max(0, valorTotal - cupomAplicado.desconto)
    : valorTotal;

  const metodosEntrega = [
    {
      id: 'dinheiro',
      nome: 'Dinheiro',
      icone: DollarSign,
      descricao: tipoEntrega === 'retirada' ? 'Pagamento em dinheiro no balcão' : 'Pagamento em dinheiro na entrega',
      cor: 'green'
    },
    {
      id: 'cartao',
      nome: 'Cartão (Débito/Crédito)',
      icone: CreditCard,
      descricao: tipoEntrega === 'retirada' ? 'Pagamento com cartão no balcão' : 'Pagamento com cartão na entrega (levar maquininha)',
      cor: 'blue'
    },
    {
      id: 'pix',
      nome: 'PIX',
      icone: QrCode,
      descricao: tipoEntrega === 'retirada' ? 'PIX presencial via QR Code' : 'PIX na entrega via celular',
      cor: 'purple'
    }
  ];

  // Retorna classes Tailwind como literais (garantindo detecção pelo JIT/purge)
  const getMetodoClasses = (metodo: { cor: string }, isSelected: boolean) => {
    if (metodo.cor === 'green') {
      return {
        label: isSelected ? 'border-green-500 bg-green-900/20' : 'border-gray-600 bg-gray-800 hover:border-green-600/50',
        icon: isSelected ? 'bg-green-500' : 'bg-gray-600',
        check: 'text-green-500'
      };
    }
    if (metodo.cor === 'blue') {
      return {
        label: isSelected ? 'border-blue-500 bg-blue-900/20' : 'border-gray-600 bg-gray-800 hover:border-blue-600/50',
        icon: isSelected ? 'bg-blue-500' : 'bg-gray-600',
        check: 'text-blue-500'
      };
    }
    // purple (PIX)
    return {
      label: isSelected ? 'border-purple-500 bg-purple-900/20' : 'border-gray-600 bg-gray-800 hover:border-purple-600/50',
      icon: isSelected ? 'bg-purple-500' : 'bg-gray-600',
      check: 'text-purple-500'
    };
  };


  const metodosSite = [
    {
      id: 'pix_site',
      nome: 'PIX',
      icone: QrCode,
      descricao: 'Pague via PIX pelo site'
    }
  ];

  // Funções de renderização (ANTES do return)
  function renderMobileModal() {
    if (!showMetodos) {
      return (
        <div
          className="fixed inset-0 bg-black/90 backdrop-blur-sm flex items-center justify-center z-[9999] p-4"
          onClick={handleOverlayClick}
          onTouchMove={handleOverlayTouchMove}
          style={{
            paddingTop: 'max(16px, env(safe-area-inset-top))',
            paddingBottom: 'max(16px, env(safe-area-inset-bottom))'
          }}
        >
          <div
            className="bg-gray-900 rounded-2xl shadow-2xl w-full max-w-md max-h-[90vh] overflow-y-auto border border-gray-700 flex flex-col"
            onClick={(e) => e.stopPropagation()}
            style={{
              WebkitOverflowScrolling: 'touch',
              overscrollBehavior: 'contain'
            }}
            data-modal-content
          >
            {/* Header */}
            <div className="flex items-center justify-between p-3 border-b border-gray-700 flex-shrink-0">
              <div className="flex items-center gap-2">
                {podeVoltar && (
                  <button
                    onClick={onVoltar}
                    className="text-gray-400 hover:text-white transition-colors p-1 min-h-[44px] min-w-[44px] flex items-center justify-center"
                    aria-label="Voltar"
                  >
                    <ArrowLeft size={20} />
                  </button>
                )}
                <h2 className="text-base font-semibold text-white">Forma de Pagamento</h2>
              </div>
              <button
                onClick={handleClose}
                className="text-gray-400 hover:text-red-400 transition-colors min-h-[44px] min-w-[44px] flex items-center justify-center"
                aria-label="Fechar"
              >
                <X size={20} />
              </button>
            </div>

            {/* Conteúdo com scroll */}
            <div className="overflow-y-auto flex-1 p-3" style={{ WebkitOverflowScrolling: 'touch' }} data-modal-content>
              {/* Campo de Cupom */}
              {validarCupom && calcularDescontoCupom && (
                <div className="mb-3 p-3 bg-gray-800 rounded-lg border border-gray-700">
                  <label className="block text-xs font-medium text-white mb-2">
                    <Tag className="inline h-3 w-3 mr-1" />
                    Cupom de Desconto
                  </label>
                  {!cupomAplicado ? (
                    <div className="flex gap-2">
                      <input
                        type="text"
                        value={codigoCupom}
                        onChange={(e) => setCodigoCupom(e.target.value.toUpperCase())}
                        onKeyPress={(e) => e.key === 'Enter' && handleAplicarCupom()}
                        placeholder="Digite o código"
                        className="flex-1 px-3 py-2.5 bg-gray-700 border border-gray-600 rounded-md focus:outline-none focus:ring-2 focus:ring-green-500 text-white placeholder-gray-400 text-sm min-h-[44px]"
                      />
                      <button
                        onClick={handleAplicarCupom}
                        disabled={!codigoCupom.trim() || validandoCupom}
                        className="px-4 py-2.5 bg-green-600 hover:bg-green-700 disabled:bg-gray-600 disabled:cursor-not-allowed text-white rounded-md font-medium transition-colors text-sm min-h-[44px]"
                      >
                        {validandoCupom ? '...' : 'Aplicar'}
                      </button>
                    </div>
                  ) : (
                    <div className="flex items-center justify-between p-2.5 bg-green-900/20 border border-green-700 rounded-lg">
                      <div className="flex items-center gap-2">
                        <Check className="h-4 w-4 text-green-400" />
                        <div>
                          <div className="text-white font-medium text-xs">
                            Cupom: {cupomAplicado.codigo}
                          </div>
                          <div className="text-green-400 text-[10px]">
                            Desconto: {cupomAplicado.promocao.tipo === 'percentual'
                              ? `${cupomAplicado.promocao.valor}%`
                              : `R$ ${cupomAplicado.promocao.valor.toFixed(2)}`}
                          </div>
                        </div>
                      </div>
                      <button
                        onClick={handleRemoverCupom}
                        className="text-red-400 hover:text-red-300 text-xs min-h-[44px] min-w-[44px] flex items-center justify-center"
                      >
                        Remover
                      </button>
                    </div>
                  )}
                </div>
              )}

              {/* Valor Total */}
              <div className="mb-4 p-3 bg-gray-800 rounded-lg border border-gray-700">
                <div className="space-y-1.5">
                  <div className="flex items-center justify-between">
                    <span className="text-gray-300 text-xs">Subtotal:</span>
                    <span className="text-base font-semibold text-white">
                      R$ {valorTotal.toFixed(2)}
                    </span>
                  </div>
                  {cupomAplicado && (
                    <div className="flex items-center justify-between text-green-400">
                      <span className="text-xs">Desconto ({cupomAplicado.codigo}):</span>
                      <span className="text-xs font-medium">
                        -R$ {cupomAplicado.desconto.toFixed(2)}
                      </span>
                    </div>
                  )}
                  <div className="flex items-center justify-between pt-1.5 border-t border-gray-700">
                    <span className="text-gray-300 font-medium text-sm">Total:</span>
                    <span className="text-xl font-bold text-green-400">
                      R$ {valorTotalComDesconto.toFixed(2)}
                    </span>
                  </div>
                </div>
              </div>

              {/* Escolha do Tipo de Pagamento */}
              <div className="mb-4">
                <h3 className="text-sm font-medium text-white mb-2">
                  Como você deseja pagar?
                </h3>

                <div className="grid grid-cols-1 gap-2">
                  <button
                    onClick={() => handleTipoPagamento('entrega')}
                    className="flex items-center p-3 border-2 rounded-lg cursor-pointer transition-all border-gray-600 active:border-green-500 bg-gray-800 active:bg-green-900/20 min-h-[60px]"
                  >
                    <div className="flex items-center space-x-2.5 w-full">
                      <div className="p-1.5 rounded-full bg-gray-600">
                        <Truck size={18} className="text-gray-300" />
                      </div>
                      <div className="flex-1 text-left">
                        <div className="font-medium text-white text-sm">
                          {tipoEntrega === 'retirada' ? 'Pagamento no Balcão' : 'Pagamento na Entrega'}
                        </div>
                        <div className="text-xs text-gray-400">PIX, Cartão ou Dinheiro</div>
                      </div>
                      <div className="text-gray-400">
                        →
                      </div>
                    </div>
                  </button>

                  <button
                    onClick={() => handleTipoPagamento('site')}
                    className="flex items-center p-3 border-2 rounded-lg cursor-pointer transition-all border-gray-600 active:border-blue-500 bg-gray-800 active:bg-blue-900/20 min-h-[60px]"
                  >
                    <div className="flex items-center space-x-2.5 w-full">
                      <div className="p-1.5 rounded-full bg-gray-600">
                        <Smartphone size={18} className="text-gray-300" />
                      </div>
                      <div className="flex-1 text-left">
                        <div className="font-medium text-white text-sm">Pagamento pelo Site</div>
                        <div className="text-xs text-gray-400">Apenas PIX - Pague agora</div>
                      </div>
                      <div className="text-gray-400">
                        →
                      </div>
                    </div>
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      );
    }

    // Modal Secundário Mobile - Métodos Específicos
    return (
      <div
        className="fixed inset-0 bg-black/90 backdrop-blur-sm flex items-center justify-center z-[9999] p-4"
        onClick={handleOverlayClick}
        onTouchMove={handleOverlayTouchMove}
        style={{
          paddingTop: 'max(16px, env(safe-area-inset-top))',
          paddingBottom: 'max(16px, env(safe-area-inset-bottom))'
        }}
      >
        <div
          className="bg-gray-900 rounded-2xl shadow-2xl w-full max-w-md max-h-[90vh] overflow-y-auto border border-gray-700 flex flex-col"
          onClick={(e) => e.stopPropagation()}
          style={{
            WebkitOverflowScrolling: 'touch',
            overscrollBehavior: 'contain'
          }}
          data-modal-content
        >
          {/* Header com Botão Voltar */}
          <div className="flex items-center justify-between p-3 border-b border-gray-700 flex-shrink-0">
            <div className="flex items-center space-x-2">
              <button
                onClick={handleVoltar}
                className="text-gray-400 hover:text-blue-400 transition-colors p-1 min-h-[44px] min-w-[44px] flex items-center justify-center"
                aria-label="Voltar"
              >
                <ArrowLeft size={18} />
              </button>
              <h2 className="text-base font-semibold text-white">
                {tipoPagamento === 'entrega' 
                  ? (tipoEntrega === 'retirada' ? 'Pagamento no Balcão' : 'Pagamento na Entrega')
                  : 'Pagamento pelo Site'}
              </h2>
            </div>
            <button
              onClick={handleClose}
              className="text-gray-400 hover:text-red-400 transition-colors min-h-[44px] min-w-[44px] flex items-center justify-center"
              aria-label="Fechar"
            >
              <X size={20} />
            </button>
          </div>

          {/* Conteúdo com scroll */}
          <div className="overflow-y-auto flex-1 p-3" style={{ WebkitOverflowScrolling: 'touch' }} data-modal-content>
            {/* Valor Total */}
            <div className="mb-4 p-3 bg-gray-800 rounded-lg border border-gray-700">
              <div className="space-y-1.5">
                <div className="flex items-center justify-between">
                  <span className="text-gray-300 text-xs">Subtotal:</span>
                  <span className="text-base font-semibold text-white">
                    R$ {valorTotal.toFixed(2)}
                  </span>
                </div>
                {cupomAplicado && (
                  <div className="flex items-center justify-between text-green-400">
                    <span className="text-xs">Desconto ({cupomAplicado.codigo}):</span>
                    <span className="text-xs font-medium">
                      -R$ {cupomAplicado.desconto.toFixed(2)}
                    </span>
                  </div>
                )}
                <div className="flex items-center justify-between pt-1.5 border-t border-gray-700">
                  <span className="text-gray-300 font-medium text-sm">Total:</span>
                  <span className="text-xl font-bold text-green-400">
                    R$ {valorTotalComDesconto.toFixed(2)}
                  </span>
                </div>
              </div>
            </div>

            {/* Métodos de Pagamento na Entrega */}
            {tipoPagamento === 'entrega' && (
              <div className="mb-4">
                <h3 className="text-sm font-medium text-white mb-2">
                  Selecione a forma de pagamento:
                </h3>

                <div className="space-y-2 mb-3">
                  {metodosEntrega.map((metodo) => {
                    const IconComponent = metodo.icone;
                    const isSelected = metodoEntrega === metodo.id;
                    const cls = getMetodoClasses(metodo, isSelected);
                    return (
                      <label
                        key={metodo.id}
                        className={`flex items-center p-3 border-2 rounded-lg cursor-pointer transition-all min-h-[60px] ${cls.label}`}
                      >
                        <input
                          type="radio"
                          name="metodoEntrega"
                          value={metodo.id}
                          checked={isSelected}
                          onChange={(e) => setMetodoEntrega(e.target.value)}
                          className="sr-only"
                        />
                        <div className="flex items-center space-x-2.5 w-full">
                          <div className={`p-1.5 rounded-full ${cls.icon}`}>
                            <IconComponent
                              size={18}
                              className={isSelected ? 'text-white' : 'text-gray-300'}
                            />
                          </div>
                          <div className="flex-1">
                            <div className="font-medium text-white text-sm">{metodo.nome}</div>
                            <div className="text-xs text-gray-400">{metodo.descricao}</div>
                          </div>
                          {isSelected && (
                            <CheckCircle size={18} className={cls.check} />
                          )}
                        </div>
                      </label>
                    );
                  })}
                </div>

                {/* Opção de Troco (apenas para dinheiro) */}
                {metodoEntrega === 'dinheiro' && (
                  <div className="p-3 bg-gray-800 rounded-lg border border-gray-700">
                    <div className="mb-3">
                      <h4 className="text-white font-medium text-sm mb-1.5 flex items-center gap-2">
                        <DollarSign size={16} className="text-green-400" />
                        Informações sobre o troco
                      </h4>
                      <p className="text-gray-400 text-xs">
                        Se você vai pagar com dinheiro, informe se precisa de troco
                      </p>
                    </div>

                    <div className="space-y-2">
                      <label className="flex items-center space-x-2.5 p-2.5 bg-gray-700 rounded-lg cursor-pointer active:bg-gray-600 transition-colors min-h-[50px]">
                        <input
                          type="radio"
                          name="troco"
                          checked={!precisaTroco}
                          onChange={() => setPrecisaTroco(false)}
                          className="text-green-500 bg-gray-600 border-gray-500 focus:ring-green-500 focus:ring-2"
                        />
                        <div>
                          <div className="text-white font-medium text-sm">Pagamento exato</div>
                          <div className="text-gray-400 text-xs">Vou pagar exatamente R$ {valorTotalComDesconto.toFixed(2)}</div>
                        </div>
                      </label>

                      <label className="flex items-center space-x-2.5 p-2.5 bg-gray-700 rounded-lg cursor-pointer active:bg-gray-600 transition-colors min-h-[50px]">
                        <input
                          type="radio"
                          name="troco"
                          checked={precisaTroco}
                          onChange={() => setPrecisaTroco(true)}
                          className="text-green-500 bg-gray-600 border-gray-500 focus:ring-green-500 focus:ring-2"
                        />
                        <div>
                          <div className="text-white font-medium text-sm">Preciso de troco</div>
                          <div className="text-gray-400 text-xs">Vou pagar com uma nota maior</div>
                        </div>
                      </label>
                    </div>

                    {precisaTroco && (
                      <div className="mt-3 p-2.5 bg-green-900/20 border border-green-700 rounded-lg">
                        <label className="block text-xs font-medium text-green-400 mb-2">
                          💰 {tipoEntrega === 'retirada' ? 'Qual valor você vai entregar no caixa?' : 'Qual valor você vai dar para o entregador?'}
                        </label>
                        <div className="space-y-2">
                          <input
                            type="number"
                            min={valorTotal}
                            step="0.01"
                            value={valorTroco || ''}
                            onChange={(e) => setValorTroco(Number(e.target.value))}
                            placeholder={`Ex: ${(valorTotal + 10).toFixed(2)}`}
                            className="w-full px-3 py-2.5 bg-gray-700 border border-gray-600 rounded-md focus:outline-none focus:ring-2 focus:ring-green-500 text-white placeholder-gray-400 text-sm min-h-[44px]"
                          />

                          {valorTroco > 0 && (
                            <div className={`rounded-lg p-2.5 ${valorTroco >= valorTotal ? 'bg-gray-800' : 'bg-red-900/20 border border-red-700'}`}>
                              <div className="flex justify-between items-center text-xs">
                                <span className="text-gray-300">Valor do pedido:</span>
                                <span className="text-white font-medium">R$ {valorTotalComDesconto.toFixed(2)}</span>
                              </div>
                              <div className="flex justify-between items-center text-xs mt-1">
                                <span className="text-gray-300">Você vai dar:</span>
                                <span className="text-blue-400 font-medium">R$ {valorTroco.toFixed(2)}</span>
                              </div>
                              <div className="flex justify-between items-center text-xs mt-1 pt-1.5 border-t border-gray-600">
                                <span className="text-green-400 font-medium">Troco:</span>
                                <span className={`font-bold text-base ${valorTroco >= valorTotalComDesconto ? 'text-green-400' : 'text-red-400'}`}>
                                  R$ {(valorTroco - valorTotalComDesconto).toFixed(2)}
                                </span>
                              </div>

                              {valorTroco >= valorTotalComDesconto && (
                                <div className="mt-2.5 p-2.5 bg-green-500/10 border border-green-500/30 rounded-lg">
                                  <p className="text-white text-xs text-center leading-relaxed">
                                    <span className="block font-bold text-green-400 mb-0.5">Resumo do Troco</span>
                                    Pedido de <span className="font-semibold">R$ {valorTotalComDesconto.toFixed(2)}</span>,
                                    pago com <span className="font-semibold text-blue-400">R$ {valorTroco.toFixed(2)}</span>.
                                    Troco de <span className="font-bold text-green-400 text-sm">R$ {(valorTroco - valorTotalComDesconto).toFixed(2)}</span>.
                                  </p>
                                </div>
                              )}

                              {valorTroco < valorTotalComDesconto && (
                                <div className="mt-2 p-2 bg-red-900/30 rounded border border-red-600">
                                  <p className="text-red-300 text-[10px]">
                                    ⚠️ O valor deve ser maior que o pedido para receber troco
                                  </p>
                                </div>
                              )}
                            </div>
                          )}

                          <p className="text-[10px] text-gray-500">
                            💡 Dica: {tipoEntrega === 'retirada' ? 'O caixa providenciará seu troco' : 'O entregador levará o troco para você'}
                          </p>
                        </div>
                      </div>
                    )}
                  </div>
                )}
              </div>
            )}

            {/* Métodos de Pagamento pelo Site */}
            {tipoPagamento === 'site' && (
              <div className="mb-4">
                <div className="p-3 bg-blue-900/20 border border-blue-700 rounded-lg">
                  <div className="flex items-center gap-2.5">
                    <div className="p-1.5 rounded-full bg-blue-500">
                      <QrCode size={18} className="text-white" />
                    </div>
                    <div>
                      <div className="font-medium text-white text-sm">PIX pelo Site</div>
                      <div className="text-xs text-blue-300">Pagamento instantâneo e seguro</div>
                    </div>
                    <CheckCircle size={18} className="text-blue-500 ml-auto" />
                  </div>
                </div>
              </div>
            )}

            {/* Botões */}
            <div className="flex space-x-2 pt-2 border-t border-gray-700 flex-shrink-0">
              <button
                onClick={handleVoltar}
                className="flex-1 px-4 py-3 text-gray-300 bg-gray-800 active:bg-gray-700 rounded-lg font-medium transition-colors border border-gray-600 active:border-gray-500 text-sm min-h-[44px]"
              >
                Voltar
              </button>
              <button
                onClick={handleConfirmarPagamento}
                disabled={!tipoPagamento || loading ||
                  (tipoPagamento === 'entrega' && !metodoEntrega) ||
                  (metodoEntrega === 'dinheiro' && precisaTroco && (!valorTroco || valorTroco < valorTotalComDesconto))
                }
                className={`flex-1 px-4 py-3 text-white font-medium rounded-lg transition-colors text-sm min-h-[44px] ${!tipoPagamento || loading ||
                  (tipoPagamento === 'entrega' && !metodoEntrega) ||
                  (metodoEntrega === 'dinheiro' && precisaTroco && (!valorTroco || valorTroco < valorTotalComDesconto))
                  ? 'bg-gray-600 cursor-not-allowed'
                  : tipoPagamento === 'entrega'
                    ? 'bg-green-600 active:bg-green-700'
                    : 'bg-blue-600 active:bg-blue-700'
                  }`}
              >
                {loading ? (
                  <div className="flex items-center justify-center">
                    <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                    Processando...
                  </div>
                ) : (
                  tipoPagamento === 'site' ? 'Pagar Agora' : (tipoEntrega === 'retirada' ? 'Confirmar Retirada' : 'Confirmar Pedido')
                )}
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  function renderTabletModal() {
    if (!showMetodos) {
      return (
        <div
          className="fixed inset-0 bg-black/80 backdrop-blur-sm flex items-center justify-center z-[9999] p-5"
          onClick={handleOverlayClick}
          style={{
            paddingTop: 'max(16px, env(safe-area-inset-top))',
            paddingBottom: 'max(16px, env(safe-area-inset-bottom))'
          }}
        >
          <div
            className="bg-gray-900 rounded-2xl shadow-2xl max-w-2xl w-full max-h-[90vh] overflow-y-auto border border-gray-700 flex flex-col"
            onClick={(e) => e.stopPropagation()}
            style={{ WebkitOverflowScrolling: 'touch' }}
            data-modal-content
          >
            {/* Header */}
            <div className="flex items-center justify-between p-5 border-b border-gray-700 flex-shrink-0">
              <div className="flex items-center gap-3">
                {podeVoltar && (
                  <button
                    onClick={onVoltar}
                    className="text-gray-400 hover:text-white transition-colors p-1"
                    aria-label="Voltar"
                  >
                    <ArrowLeft size={22} />
                  </button>
                )}
                <h2 className="text-lg font-semibold text-white">Forma de Pagamento</h2>
              </div>
              <button
                onClick={handleClose}
                className="text-gray-400 hover:text-red-400 transition-colors min-h-[44px] min-w-[44px] flex items-center justify-center"
                aria-label="Fechar"
              >
                <X size={22} />
              </button>
            </div>

            {/* Conteúdo */}
            <div className="p-5 overflow-y-auto flex-1" data-modal-content>
              {/* Campo de Cupom */}
              {validarCupom && calcularDescontoCupom && (
                <div className="mb-4 p-4 bg-gray-800 rounded-lg border border-gray-700">
                  <label className="block text-sm font-medium text-white mb-2">
                    <Tag className="inline h-4 w-4 mr-1" />
                    Cupom de Desconto
                  </label>
                  {!cupomAplicado ? (
                    <div className="flex gap-2">
                      <input
                        type="text"
                        value={codigoCupom}
                        onChange={(e) => setCodigoCupom(e.target.value.toUpperCase())}
                        onKeyPress={(e) => e.key === 'Enter' && handleAplicarCupom()}
                        placeholder="Digite o código do cupom"
                        className="flex-1 px-3 py-2.5 bg-gray-700 border border-gray-600 rounded-md focus:outline-none focus:ring-2 focus:ring-green-500 text-white placeholder-gray-400 text-sm min-h-[44px]"
                      />
                      <button
                        onClick={handleAplicarCupom}
                        disabled={!codigoCupom.trim() || validandoCupom}
                        className="px-4 py-2.5 bg-green-600 hover:bg-green-700 disabled:bg-gray-600 disabled:cursor-not-allowed text-white rounded-md font-medium transition-colors text-sm min-h-[44px]"
                      >
                        {validandoCupom ? '...' : 'Aplicar'}
                      </button>
                    </div>
                  ) : (
                    <div className="flex items-center justify-between p-3 bg-green-900/20 border border-green-700 rounded-lg">
                      <div className="flex items-center gap-2">
                        <Check className="h-5 w-5 text-green-400" />
                        <div>
                          <div className="text-white font-medium text-sm">
                            Cupom: {cupomAplicado.codigo}
                          </div>
                          <div className="text-green-400 text-xs">
                            Desconto: {cupomAplicado.promocao.tipo === 'percentual'
                              ? `${cupomAplicado.promocao.valor}%`
                              : `R$ ${cupomAplicado.promocao.valor.toFixed(2)}`}
                          </div>
                        </div>
                      </div>
                      <button
                        onClick={handleRemoverCupom}
                        className="text-red-400 hover:text-red-300 text-sm min-h-[44px] min-w-[44px] flex items-center justify-center"
                      >
                        Remover
                      </button>
                    </div>
                  )}
                </div>
              )}

              {/* Valor Total */}
              <div className="mb-5 p-4 bg-gray-800 rounded-lg border border-gray-700">
                <div className="space-y-2">
                  <div className="flex items-center justify-between">
                    <span className="text-gray-300">Subtotal:</span>
                    <span className="text-lg font-semibold text-white">
                      R$ {valorTotal.toFixed(2)}
                    </span>
                  </div>
                  {cupomAplicado && (
                    <div className="flex items-center justify-between text-green-400">
                      <span className="text-sm">Desconto ({cupomAplicado.codigo}):</span>
                      <span className="text-sm font-medium">
                        -R$ {cupomAplicado.desconto.toFixed(2)}
                      </span>
                    </div>
                  )}
                  <div className="flex items-center justify-between pt-2 border-t border-gray-700">
                    <span className="text-gray-300 font-medium">Total:</span>
                    <span className="text-2xl font-bold text-green-400">
                      R$ {valorTotalComDesconto.toFixed(2)}
                    </span>
                  </div>
                </div>
              </div>

              {/* Escolha do Tipo de Pagamento */}
              <div className="mb-5">
                <h3 className="text-base font-medium text-white mb-3">
                  Como você deseja pagar?
                </h3>

                <div className="grid grid-cols-1 gap-3">
                  <button
                    onClick={() => handleTipoPagamento('entrega')}
                    className="flex items-center p-4 border-2 rounded-lg cursor-pointer transition-all border-gray-600 hover:border-green-500 bg-gray-800 hover:bg-green-900/20 group min-h-[70px]"
                  >
                    <div className="flex items-center space-x-3 w-full">
                      <div className="p-2 rounded-full bg-gray-600 group-hover:bg-green-500 transition-colors">
                        <Truck size={20} className="text-gray-300 group-hover:text-white transition-colors" />
                      </div>
                      <div className="flex-1 text-left">
                        <div className="font-medium text-white">
                          {tipoEntrega === 'retirada' ? 'Pagamento no Balcão' : 'Pagamento na Entrega'}
                        </div>
                        <div className="text-sm text-gray-400">PIX, Cartão (Débito/Crédito) ou Dinheiro</div>
                      </div>
                      <div className="text-gray-400 group-hover:text-green-500 transition-colors">
                        →
                      </div>
                    </div>
                  </button>

                  <button
                    onClick={() => handleTipoPagamento('site')}
                    className="flex items-center p-4 border-2 rounded-lg cursor-pointer transition-all border-gray-600 hover:border-blue-500 bg-gray-800 hover:bg-blue-900/20 group min-h-[70px]"
                  >
                    <div className="flex items-center space-x-3 w-full">
                      <div className="p-2 rounded-full bg-gray-600 group-hover:bg-blue-500 transition-colors">
                        <Smartphone size={20} className="text-gray-300 group-hover:text-white transition-colors" />
                      </div>
                      <div className="flex-1 text-left">
                        <div className="font-medium text-white">Pagamento pelo Site</div>
                        <div className="text-sm text-gray-400">Apenas PIX - Pague agora</div>
                      </div>
                      <div className="text-gray-400 group-hover:text-blue-500 transition-colors">
                        →
                      </div>
                    </div>
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      );
    }

    // Modal Secundário Tablet - Métodos Específicos
    return (
      <div
        className="fixed inset-0 bg-black/80 backdrop-blur-sm flex items-center justify-center z-[9999] p-5"
        onClick={handleOverlayClick}
        style={{
          paddingTop: 'max(16px, env(safe-area-inset-top))',
          paddingBottom: 'max(16px, env(safe-area-inset-bottom))'
        }}
      >
        <div
          className="bg-gray-900 rounded-2xl shadow-2xl max-w-2xl w-full max-h-[90vh] overflow-y-auto border border-gray-700 flex flex-col"
          onClick={(e) => e.stopPropagation()}
          style={{ WebkitOverflowScrolling: 'touch' }}
          data-modal-content
        >
          {/* Header com Botão Voltar */}
          <div className="flex items-center justify-between p-5 border-b border-gray-700 flex-shrink-0">
            <div className="flex items-center space-x-3">
              <button
                onClick={handleVoltar}
                className="text-gray-400 hover:text-blue-400 transition-colors p-1 min-h-[44px] min-w-[44px] flex items-center justify-center"
                aria-label="Voltar"
              >
                <ArrowLeft size={20} />
              </button>
              <h2 className="text-lg font-semibold text-white">
                {tipoPagamento === 'entrega' 
                  ? (tipoEntrega === 'retirada' ? 'Pagamento no Balcão' : 'Pagamento na Entrega')
                  : 'Pagamento pelo Site'}
              </h2>
            </div>
            <button
              onClick={handleClose}
              className="text-gray-400 hover:text-red-400 transition-colors min-h-[44px] min-w-[44px] flex items-center justify-center"
              aria-label="Fechar"
            >
              <X size={22} />
            </button>
          </div>

          {/* Conteúdo */}
          <div className="p-5 overflow-y-auto flex-1" data-modal-content>
            {/* Valor Total */}
            <div className="mb-5 p-4 bg-gray-800 rounded-lg border border-gray-700">
              <div className="space-y-2">
                <div className="flex items-center justify-between">
                  <span className="text-gray-300">Subtotal:</span>
                  <span className="text-lg font-semibold text-white">
                    R$ {valorTotal.toFixed(2)}
                  </span>
                </div>
                {cupomAplicado && (
                  <div className="flex items-center justify-between text-green-400">
                    <span className="text-sm">Desconto ({cupomAplicado.codigo}):</span>
                    <span className="text-sm font-medium">
                      -R$ {cupomAplicado.desconto.toFixed(2)}
                    </span>
                  </div>
                )}
                <div className="flex items-center justify-between pt-2 border-t border-gray-700">
                  <span className="text-gray-300 font-medium">Total:</span>
                  <span className="text-2xl font-bold text-green-400">
                    R$ {valorTotalComDesconto.toFixed(2)}
                  </span>
                </div>
              </div>
            </div>

            {/* Métodos de Pagamento na Entrega */}
            {tipoPagamento === 'entrega' && (
              <div className="mb-5">
                <h3 className="text-base font-medium text-white mb-3">
                  Selecione a forma de pagamento na entrega:
                </h3>

                <div className="space-y-3 mb-4">
                  {metodosEntrega.map((metodo) => {
                    const IconComponent = metodo.icone;
                    return (
                      <label
                        key={metodo.id}
                        className={`flex items-center p-4 border-2 rounded-lg cursor-pointer transition-all min-h-[70px] ${getMetodoClasses(metodo, metodoEntrega === metodo.id).label}`}
                      >
                        <input
                          type="radio"
                          name="metodoEntrega"
                          value={metodo.id}
                          checked={metodoEntrega === metodo.id}
                          onChange={(e) => setMetodoEntrega(e.target.value)}
                          className="sr-only"
                        />
                        <div className="flex items-center space-x-3 w-full">
                          <div className={`p-2 rounded-full ${getMetodoClasses(metodo, metodoEntrega === metodo.id).icon}`}>
                            <IconComponent
                              size={20}
                              className={metodoEntrega === metodo.id ? 'text-white' : 'text-gray-300'}
                            />
                          </div>
                          <div className="flex-1">
                            <div className="font-medium text-white">{metodo.nome}</div>
                            <div className="text-sm text-gray-400">{metodo.descricao}</div>
                          </div>
                          {metodoEntrega === metodo.id && (
                            <CheckCircle size={20} className={getMetodoClasses(metodo, true).check} />
                          )}
                        </div>
                      </label>
                    );
                  })}
                </div>

                {/* Opção de Troco (apenas para dinheiro) */}
                {metodoEntrega === 'dinheiro' && (
                  <div className="p-4 bg-gray-800 rounded-lg border border-gray-700">
                    <div className="mb-4">
                      <h4 className="text-white font-medium mb-2 flex items-center gap-2">
                        <DollarSign size={18} className="text-green-400" />
                        Informações sobre o troco
                      </h4>
                      <p className="text-gray-400 text-sm">
                        Se você vai pagar com dinheiro, informe se precisa de troco
                      </p>
                    </div>

                    <div className="space-y-3">
                      <label className="flex items-center space-x-3 p-3 bg-gray-700 rounded-lg cursor-pointer hover:bg-gray-600 transition-colors min-h-[60px]">
                        <input
                          type="radio"
                          name="troco"
                          checked={!precisaTroco}
                          onChange={() => setPrecisaTroco(false)}
                          className="text-green-500 bg-gray-600 border-gray-500 focus:ring-green-500 focus:ring-2"
                        />
                        <div>
                          <div className="text-white font-medium">Pagamento exato</div>
                          <div className="text-gray-400 text-sm">Vou pagar exatamente R$ {valorTotalComDesconto.toFixed(2)}</div>
                        </div>
                      </label>

                      <label className="flex items-center space-x-3 p-3 bg-gray-700 rounded-lg cursor-pointer hover:bg-gray-600 transition-colors min-h-[60px]">
                        <input
                          type="radio"
                          name="troco"
                          checked={precisaTroco}
                          onChange={() => setPrecisaTroco(true)}
                          className="text-green-500 bg-gray-600 border-gray-500 focus:ring-green-500 focus:ring-2"
                        />
                        <div>
                          <div className="text-white font-medium">Preciso de troco</div>
                          <div className="text-gray-400 text-sm">Vou pagar com uma nota maior</div>
                        </div>
                      </label>
                    </div>

                    {precisaTroco && (
                      <div className="mt-4 p-3 bg-green-900/20 border border-green-700 rounded-lg">
                        <label className="block text-sm font-medium text-green-400 mb-2">
                          💰 {tipoEntrega === 'retirada' ? 'Qual valor você vai entregar no caixa?' : 'Qual valor você vai dar para o entregador?'}
                        </label>
                        <div className="space-y-2">
                          <input
                            type="number"
                            min={valorTotal}
                            step="0.01"
                            value={valorTroco || ''}
                            onChange={(e) => setValorTroco(Number(e.target.value))}
                            placeholder={`Ex: ${(valorTotal + 10).toFixed(2)}`}
                            className="w-full px-3 py-2.5 bg-gray-700 border border-gray-600 rounded-md focus:outline-none focus:ring-2 focus:ring-green-500 text-white placeholder-gray-400 min-h-[44px]"
                          />

                          {valorTroco > 0 && (
                            <div className={`rounded-lg p-3 ${valorTroco >= valorTotal ? 'bg-gray-800' : 'bg-red-900/20 border border-red-700'}`}>
                              <div className="flex justify-between items-center text-sm">
                                <span className="text-gray-300">Valor do pedido:</span>
                                <span className="text-white font-medium">R$ {valorTotalComDesconto.toFixed(2)}</span>
                              </div>
                              <div className="flex justify-between items-center text-sm mt-1">
                                <span className="text-gray-300">Você vai dar:</span>
                                <span className="text-blue-400 font-medium">R$ {valorTroco.toFixed(2)}</span>
                              </div>
                              <div className="flex justify-between items-center text-sm mt-1 pt-2 border-t border-gray-600">
                                <span className="text-green-400 font-medium">Troco:</span>
                                <span className={`font-bold text-lg ${valorTroco >= valorTotalComDesconto ? 'text-green-400' : 'text-red-400'}`}>
                                  R$ {(valorTroco - valorTotalComDesconto).toFixed(2)}
                                </span>
                              </div>

                              {valorTroco >= valorTotalComDesconto && (
                                <div className="mt-3 p-3 bg-green-500/10 border border-green-500/30 rounded-lg">
                                  <p className="text-white text-sm text-center leading-relaxed">
                                    <span className="block font-bold text-green-400 mb-1">Resumo do Troco</span>
                                    Pedido de <span className="font-semibold">R$ {valorTotalComDesconto.toFixed(2)}</span>,
                                    pago com <span className="font-semibold text-blue-400">R$ {valorTroco.toFixed(2)}</span>.
                                    Troco de <span className="font-bold text-green-400 text-base">R$ {(valorTroco - valorTotalComDesconto).toFixed(2)}</span>.
                                  </p>
                                </div>
                              )}

                              {valorTroco < valorTotalComDesconto && (
                                <div className="mt-2 p-2 bg-red-900/30 rounded border border-red-600">
                                  <p className="text-red-300 text-xs">
                                    ⚠️ O valor deve ser maior que o pedido para receber troco
                                  </p>
                                </div>
                              )}
                            </div>
                          )}

                          <p className="text-xs text-gray-500">
                            💡 Dica: {tipoEntrega === 'retirada' ? 'O caixa providenciará seu troco' : 'O entregador levará o troco para você'}
                          </p>
                        </div>
                      </div>
                    )}
                  </div>
                )}
              </div>
            )}

            {/* Métodos de Pagamento pelo Site */}
            {tipoPagamento === 'site' && (
              <div className="mb-5">
                <div className="p-4 bg-blue-900/20 border border-blue-700 rounded-lg">
                  <div className="flex items-center gap-3">
                    <div className="p-2 rounded-full bg-blue-500">
                      <QrCode size={20} className="text-white" />
                    </div>
                    <div>
                      <div className="font-medium text-white">PIX pelo Site</div>
                      <div className="text-sm text-blue-300">Pagamento instantâneo e seguro</div>
                    </div>
                    <CheckCircle size={20} className="text-blue-500 ml-auto" />
                  </div>
                </div>
              </div>
            )}

            {/* Botões */}
            <div className="flex space-x-3 pt-4 border-t border-gray-700 flex-shrink-0">
              <button
                onClick={handleVoltar}
                className="flex-1 px-4 py-3 text-gray-300 bg-gray-800 hover:bg-gray-700 rounded-lg font-medium transition-colors border border-gray-600 hover:border-gray-500 min-h-[44px]"
              >
                Voltar
              </button>
              <button
                onClick={handleConfirmarPagamento}
                disabled={!tipoPagamento || loading ||
                  (tipoPagamento === 'entrega' && !metodoEntrega) ||
                  (metodoEntrega === 'dinheiro' && precisaTroco && (!valorTroco || valorTroco < valorTotalComDesconto))
                }
                className={`flex-1 px-4 py-3 text-white font-medium rounded-lg transition-colors min-h-[44px] ${!tipoPagamento || loading ||
                  (tipoPagamento === 'entrega' && !metodoEntrega) ||
                  (metodoEntrega === 'dinheiro' && precisaTroco && (!valorTroco || valorTroco < valorTotalComDesconto))
                  ? 'bg-gray-600 cursor-not-allowed'
                  : tipoPagamento === 'entrega'
                    ? 'bg-green-600 hover:bg-green-700'
                    : 'bg-blue-600 hover:bg-blue-700'
                  }`}
              >
                {loading ? (
                  <div className="flex items-center justify-center">
                    <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white mr-2"></div>
                    Processando...
                  </div>
                ) : (
                  tipoPagamento === 'site' ? 'Pagar Agora' : (tipoEntrega === 'retirada' ? 'Confirmar Retirada' : 'Confirmar Pedido')
                )}
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  function renderDesktopModal() {
    if (!showMetodos) {
      return (
        <div
          className="fixed inset-0 bg-black/70 backdrop-blur-sm flex items-center justify-center z-[9999] p-4"
          onClick={handleOverlayClick}
        >
          <div
            className="bg-gray-900 rounded-lg shadow-2xl max-w-lg w-full max-h-[90vh] overflow-y-auto border border-gray-700 flex flex-col"
            onClick={(e) => e.stopPropagation()}
            data-modal-content
          >
            {/* Header */}
            <div className="flex items-center justify-between p-6 border-b border-gray-700 flex-shrink-0">
              <div className="flex items-center gap-4">
                {podeVoltar && (
                  <button
                    onClick={onVoltar}
                    className="text-gray-400 hover:text-white transition-colors"
                    aria-label="Voltar"
                  >
                    <ArrowLeft size={24} />
                  </button>
                )}
                <h2 className="text-xl font-semibold text-white">Forma de Pagamento</h2>
              </div>
              <button
                onClick={handleClose}
                className="text-gray-400 hover:text-red-400 transition-colors"
                aria-label="Fechar"
              >
                <X size={24} />
              </button>
            </div>

            {/* Conteúdo */}
            <div className="p-6 overflow-y-auto flex-1" data-modal-content>
              {/* Campo de Cupom */}
              {validarCupom && calcularDescontoCupom && (
                <div className="mb-4 p-4 bg-gray-800 rounded-lg border border-gray-700">
                  <label className="block text-sm font-medium text-white mb-2">
                    <Tag className="inline h-4 w-4 mr-1" />
                    Cupom de Desconto
                  </label>
                  {!cupomAplicado ? (
                    <div className="flex gap-2">
                      <input
                        type="text"
                        value={codigoCupom}
                        onChange={(e) => setCodigoCupom(e.target.value.toUpperCase())}
                        onKeyPress={(e) => e.key === 'Enter' && handleAplicarCupom()}
                        placeholder="Digite o código do cupom"
                        className="flex-1 px-3 py-2 bg-gray-700 border border-gray-600 rounded-md focus:outline-none focus:ring-2 focus:ring-green-500 text-white placeholder-gray-400 text-sm"
                      />
                      <button
                        onClick={handleAplicarCupom}
                        disabled={!codigoCupom.trim() || validandoCupom}
                        className="px-4 py-2 bg-green-600 hover:bg-green-700 disabled:bg-gray-600 disabled:cursor-not-allowed text-white rounded-md font-medium transition-colors text-sm"
                      >
                        {validandoCupom ? '...' : 'Aplicar'}
                      </button>
                    </div>
                  ) : (
                    <div className="flex items-center justify-between p-3 bg-green-900/20 border border-green-700 rounded-lg">
                      <div className="flex items-center gap-2">
                        <Check className="h-5 w-5 text-green-400" />
                        <div>
                          <div className="text-white font-medium text-sm">
                            Cupom: {cupomAplicado.codigo}
                          </div>
                          <div className="text-green-400 text-xs">
                            Desconto: {cupomAplicado.promocao.tipo === 'percentual'
                              ? `${cupomAplicado.promocao.valor}%`
                              : `R$ ${cupomAplicado.promocao.valor.toFixed(2)}`}
                          </div>
                        </div>
                      </div>
                      <button
                        onClick={handleRemoverCupom}
                        className="text-red-400 hover:text-red-300 text-sm"
                      >
                        Remover
                      </button>
                    </div>
                  )}
                </div>
              )}

              {/* Valor Total */}
              <div className="mb-6 p-4 bg-gray-800 rounded-lg border border-gray-700">
                <div className="space-y-2">
                  <div className="flex items-center justify-between">
                    <span className="text-gray-300">Subtotal:</span>
                    <span className="text-lg font-semibold text-white">
                      R$ {valorTotal.toFixed(2)}
                    </span>
                  </div>
                  {cupomAplicado && (
                    <div className="flex items-center justify-between text-green-400">
                      <span className="text-sm">Desconto ({cupomAplicado.codigo}):</span>
                      <span className="text-sm font-medium">
                        -R$ {cupomAplicado.desconto.toFixed(2)}
                      </span>
                    </div>
                  )}
                  <div className="flex items-center justify-between pt-2 border-t border-gray-700">
                    <span className="text-gray-300 font-medium">Total:</span>
                    <span className="text-2xl font-bold text-green-400">
                      R$ {valorTotalComDesconto.toFixed(2)}
                    </span>
                  </div>
                </div>
              </div>

              {/* Escolha do Tipo de Pagamento */}
              <div className="mb-6">
                <h3 className="text-lg font-medium text-white mb-3">
                  Como você deseja pagar?
                </h3>

                <div className="grid grid-cols-1 gap-3">
                  <button
                    onClick={() => handleTipoPagamento('entrega')}
                    className="flex items-center p-4 border-2 rounded-lg cursor-pointer transition-all border-gray-600 hover:border-green-500 bg-gray-800 hover:bg-green-900/20 group"
                  >
                    <div className="flex items-center space-x-3">
                      <div className="p-2 rounded-full bg-gray-600 group-hover:bg-green-500 transition-colors">
                        <Truck
                          size={20}
                          className="text-gray-300 group-hover:text-white transition-colors"
                        />
                      </div>
                      <div className="flex-1 text-left">
                        <div className="font-medium text-white">
                          {tipoEntrega === 'retirada' ? 'Pagamento no Balcão' : 'Pagamento na Entrega'}
                        </div>
                        <div className="text-sm text-gray-400">PIX, Cartão (Débito/Crédito) ou Dinheiro</div>
                      </div>
                      <div className="text-gray-400 group-hover:text-green-500 transition-colors">
                        →
                      </div>
                    </div>
                  </button>

                  <button
                    onClick={() => handleTipoPagamento('site')}
                    className="flex items-center p-4 border-2 rounded-lg cursor-pointer transition-all border-gray-600 hover:border-blue-500 bg-gray-800 hover:bg-blue-900/20 group"
                  >
                    <div className="flex items-center space-x-3">
                      <div className="p-2 rounded-full bg-gray-600 group-hover:bg-blue-500 transition-colors">
                        <Smartphone
                          size={20}
                          className="text-gray-300 group-hover:text-white transition-colors"
                        />
                      </div>
                      <div className="flex-1 text-left">
                        <div className="font-medium text-white">Pagamento pelo Site</div>
                        <div className="text-sm text-gray-400">Apenas PIX - Pague agora</div>
                      </div>
                      <div className="text-gray-400 group-hover:text-blue-500 transition-colors">
                        →
                      </div>
                    </div>
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      );
    }

    // Modal Secundário Desktop - Métodos Específicos
    return (
      <div
        className="fixed inset-0 bg-black/70 backdrop-blur-sm flex items-center justify-center z-[9999] p-4"
        onClick={handleOverlayClick}
      >
        <div
          className="bg-gray-900 rounded-lg shadow-2xl max-w-lg w-full max-h-[90vh] overflow-y-auto border border-gray-700 flex flex-col"
          onClick={(e) => e.stopPropagation()}
          data-modal-content
        >
          {/* Header com Botão Voltar */}
          <div className="flex items-center justify-between p-6 border-b border-gray-700 flex-shrink-0">
            <div className="flex items-center space-x-3">
              <button
                onClick={handleVoltar}
                className="text-gray-400 hover:text-blue-400 transition-colors p-1"
                aria-label="Voltar"
              >
                <ArrowLeft size={20} />
              </button>
              <h2 className="text-xl font-semibold text-white">
                {tipoPagamento === 'entrega' 
                  ? (tipoEntrega === 'retirada' ? 'Pagamento no Balcão' : 'Pagamento na Entrega')
                  : 'Pagamento pelo Site'}
              </h2>
            </div>
            <button
              onClick={handleClose}
              className="text-gray-400 hover:text-red-400 transition-colors"
              aria-label="Fechar"
            >
              <X size={24} />
            </button>
          </div>

          {/* Conteúdo */}
          <div className="p-6 overflow-y-auto flex-1" data-modal-content>
            {/* Valor Total */}
            <div className="mb-6 p-4 bg-gray-800 rounded-lg border border-gray-700">
              <div className="space-y-2">
                <div className="flex items-center justify-between">
                  <span className="text-gray-300">Subtotal:</span>
                  <span className="text-lg font-semibold text-white">
                    R$ {valorTotal.toFixed(2)}
                  </span>
                </div>
                {cupomAplicado && (
                  <div className="flex items-center justify-between text-green-400">
                    <span className="text-sm">Desconto ({cupomAplicado.codigo}):</span>
                    <span className="text-sm font-medium">
                      -R$ {cupomAplicado.desconto.toFixed(2)}
                    </span>
                  </div>
                )}
                <div className="flex items-center justify-between pt-2 border-t border-gray-700">
                  <span className="text-gray-300 font-medium">Total:</span>
                  <span className="text-2xl font-bold text-green-400">
                    R$ {valorTotalComDesconto.toFixed(2)}
                  </span>
                </div>
              </div>
            </div>

            {/* Métodos de Pagamento na Entrega */}
            {tipoPagamento === 'entrega' && (
              <div className="mb-6">
                <h3 className="text-lg font-medium text-white mb-3">
                  {tipoEntrega === 'retirada' ? 'Selecione a forma de pagamento no balcão:' : 'Selecione a forma de pagamento na entrega:'}
                </h3>

                <div className="space-y-3 mb-4">
                  {metodosEntrega.map((metodo) => {
                    const IconComponent = metodo.icone;
                    return (
                      <label
                        key={metodo.id}
                        className={`flex items-center p-4 border-2 rounded-lg cursor-pointer transition-all ${getMetodoClasses(metodo, metodoEntrega === metodo.id).label}`}
                      >
                        <input
                          type="radio"
                          name="metodoEntrega"
                          value={metodo.id}
                          checked={metodoEntrega === metodo.id}
                          onChange={(e) => setMetodoEntrega(e.target.value)}
                          className="sr-only"
                        />
                        <div className="flex items-center space-x-3">
                          <div className={`p-2 rounded-full ${getMetodoClasses(metodo, metodoEntrega === metodo.id).icon}`}>
                            <IconComponent
                              size={20}
                              className={metodoEntrega === metodo.id ? 'text-white' : 'text-gray-300'}
                            />
                          </div>
                          <div className="flex-1">
                            <div className="font-medium text-white">{metodo.nome}</div>
                            <div className="text-sm text-gray-400">{metodo.descricao}</div>
                          </div>
                          {metodoEntrega === metodo.id && (
                            <CheckCircle size={20} className={getMetodoClasses(metodo, true).check} />
                          )}
                        </div>
                      </label>
                    );
                  })}
                </div>

                {/* Opção de Troco (apenas para dinheiro) */}
                {metodoEntrega === 'dinheiro' && (
                  <div className="p-4 bg-gray-800 rounded-lg border border-gray-700">
                    <div className="mb-4">
                      <h4 className="text-white font-medium mb-2 flex items-center gap-2">
                        <DollarSign size={18} className="text-green-400" />
                        Informações sobre o troco
                      </h4>
                      <p className="text-gray-400 text-sm">
                        Se você vai pagar com dinheiro, informe se precisa de troco
                      </p>
                    </div>

                    <div className="space-y-3">
                      <label className="flex items-center space-x-3 p-3 bg-gray-700 rounded-lg cursor-pointer hover:bg-gray-600 transition-colors">
                        <input
                          type="radio"
                          name="troco"
                          checked={!precisaTroco}
                          onChange={() => setPrecisaTroco(false)}
                          className="text-green-500 bg-gray-600 border-gray-500 focus:ring-green-500 focus:ring-2"
                        />
                        <div>
                          <div className="text-white font-medium">Pagamento exato</div>
                          <div className="text-gray-400 text-sm">Vou pagar exatamente R$ {valorTotalComDesconto.toFixed(2)}</div>
                        </div>
                      </label>

                      <label className="flex items-center space-x-3 p-3 bg-gray-700 rounded-lg cursor-pointer hover:bg-gray-600 transition-colors">
                        <input
                          type="radio"
                          name="troco"
                          checked={precisaTroco}
                          onChange={() => setPrecisaTroco(true)}
                          className="text-green-500 bg-gray-600 border-gray-500 focus:ring-green-500 focus:ring-2"
                        />
                        <div>
                          <div className="text-white font-medium">Preciso de troco</div>
                          <div className="text-gray-400 text-sm">Vou pagar com uma nota maior</div>
                        </div>
                      </label>
                    </div>

                    {precisaTroco && (
                      <div className="mt-4 p-3 bg-green-900/20 border border-green-700 rounded-lg">
                        <label className="block text-sm font-medium text-green-400 mb-2">
                          💰 {tipoEntrega === 'retirada' ? 'Qual valor você vai entregar no caixa?' : 'Qual valor você vai dar para o entregador?'}
                        </label>
                        <div className="space-y-2">
                          <input
                            type="number"
                            min={valorTotal}
                            step="0.01"
                            value={valorTroco || ''}
                            onChange={(e) => setValorTroco(Number(e.target.value))}
                            placeholder={`Ex: ${(valorTotal + 10).toFixed(2)}`}
                            className="w-full px-3 py-2 bg-gray-700 border border-gray-600 rounded-md focus:outline-none focus:ring-2 focus:ring-green-500 text-white placeholder-gray-400"
                          />

                          {valorTroco > 0 && (
                            <div className={`rounded-lg p-3 ${valorTroco >= valorTotal ? 'bg-gray-800' : 'bg-red-900/20 border border-red-700'}`}>
                              <div className="flex justify-between items-center text-sm">
                                <span className="text-gray-300">Valor do pedido:</span>
                                <span className="text-white font-medium">R$ {valorTotalComDesconto.toFixed(2)}</span>
                              </div>
                              <div className="flex justify-between items-center text-sm mt-1">
                                <span className="text-gray-300">Você vai dar:</span>
                                <span className="text-blue-400 font-medium">R$ {valorTroco.toFixed(2)}</span>
                              </div>
                              <div className="flex justify-between items-center text-sm mt-1 pt-2 border-t border-gray-600">
                                <span className="text-green-400 font-medium">Troco:</span>
                                <span className={`font-bold text-lg ${valorTroco >= valorTotalComDesconto ? 'text-green-400' : 'text-red-400'}`}>
                                  R$ {(valorTroco - valorTotalComDesconto).toFixed(2)}
                                </span>
                              </div>

                              {valorTroco >= valorTotalComDesconto && (
                                <div className="mt-3 p-3 bg-green-500/10 border border-green-500/30 rounded-lg">
                                  <p className="text-white text-sm text-center leading-relaxed">
                                    <span className="block font-bold text-green-400 mb-1">Resumo do Troco</span>
                                    Pedido de <span className="font-semibold">R$ {valorTotalComDesconto.toFixed(2)}</span>,
                                    pago com <span className="font-semibold text-blue-400">R$ {valorTroco.toFixed(2)}</span>.
                                    Troco de <span className="font-bold text-green-400 text-base">R$ {(valorTroco - valorTotalComDesconto).toFixed(2)}</span>.
                                  </p>
                                </div>
                              )}

                              {valorTroco < valorTotalComDesconto && (
                                <div className="mt-2 p-2 bg-red-900/30 rounded border border-red-600">
                                  <p className="text-red-300 text-xs">
                                    ⚠️ O valor deve ser maior que o pedido para receber troco
                                  </p>
                                </div>
                              )}
                            </div>
                          )}

                          <p className="text-xs text-gray-500">
                            💡 Dica: {tipoEntrega === 'retirada' ? 'O caixa providenciará seu troco' : 'O entregador levará o troco para você'}
                          </p>
                        </div>
                      </div>
                    )}
                  </div>
                )}
              </div>
            )}

            {/* Métodos de Pagamento pelo Site */}
            {tipoPagamento === 'site' && (
              <div className="mb-6">
                <div className="p-4 bg-blue-900/20 border border-blue-700 rounded-lg">
                  <div className="flex items-center gap-3">
                    <div className="p-2 rounded-full bg-blue-500">
                      <QrCode size={20} className="text-white" />
                    </div>
                    <div>
                      <div className="font-medium text-white">PIX pelo Site</div>
                      <div className="text-sm text-blue-300">Pagamento instantâneo e seguro</div>
                    </div>
                    <CheckCircle size={20} className="text-blue-500 ml-auto" />
                  </div>
                </div>
              </div>
            )}

            {/* Botões */}
            <div className="flex space-x-3 flex-shrink-0">
              <button
                onClick={handleVoltar}
                className="flex-1 px-4 py-3 text-gray-300 bg-gray-800 hover:bg-gray-700 rounded-lg font-medium transition-colors border border-gray-600 hover:border-gray-500"
              >
                Voltar
              </button>
              <button
                onClick={handleConfirmarPagamento}
                disabled={!tipoPagamento || loading ||
                  (tipoPagamento === 'entrega' && !metodoEntrega) ||
                  (metodoEntrega === 'dinheiro' && precisaTroco && (!valorTroco || valorTroco < valorTotalComDesconto))
                }
                className={`flex-1 px-4 py-3 text-white font-medium rounded-lg transition-colors ${!tipoPagamento || loading ||
                  (tipoPagamento === 'entrega' && !metodoEntrega) ||
                  (metodoEntrega === 'dinheiro' && precisaTroco && (!valorTroco || valorTroco < valorTotalComDesconto))
                  ? 'bg-gray-600 cursor-not-allowed'
                  : tipoPagamento === 'entrega'
                    ? 'bg-green-600 hover:bg-green-700'
                    : 'bg-blue-600 hover:bg-blue-700'
                  }`}
              >
                {loading ? (
                  <div className="flex items-center justify-center">
                    <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white mr-2"></div>
                    Processando...
                  </div>
                ) : (
                  tipoPagamento === 'site' ? 'Pagar Agora' : (tipoEntrega === 'retirada' ? 'Confirmar Retirada' : 'Confirmar Pedido')
                )}
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // Lógica de renderização condicional (DEPOIS das funções)
  // Renderizar checkout do Mercado Pago se necessário (antes de qualquer outro render)
  if (showMercadoPago) {
    return (
      <MercadoPagoCheckout
        isOpen={showMercadoPago}
        onClose={() => setShowMercadoPago(false)}
        pedido={{
          total: valorTotal,
          email_cliente: usuario?.email || 'cliente@exemplo.com'
        }}
        onPagamentoConfirmado={handlePagamentoMercadoPago}
        dadosCarrinho={dadosCarrinho}
      />
    );
  }

  if (!isOpen) return null;

  // Renderizar versão específica por dispositivo
  if (deviceType === 'mobile') {
    return renderMobileModal();
  } else if (deviceType === 'tablet') {
    return renderTabletModal();
  } else {
    return renderDesktopModal();
  }
};

export default ModalPagamento;
